/**
 * Device enumeration + preflight helpers shared by the therapist and client
 * front ends.
 */
export async function listDevices() {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return {
    cameras: devices.filter((d) => d.kind === 'videoinput'),
    microphones: devices.filter((d) => d.kind === 'audioinput'),
    speakers: devices.filter((d) => d.kind === 'audiooutput'),
  };
}

export function populateDeviceSelect(selectEl, devices, kind) {
  selectEl.innerHTML = '';
  devices.forEach((d, i) => {
    const opt = document.createElement('option');
    opt.value = d.deviceId;
    opt.textContent = d.label || `${kind} ${i + 1}`;
    selectEl.appendChild(opt);
  });
}

export async function requestPreflightStream({ videoDeviceId, audioDeviceId } = {}) {
  const constraints = {
    audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
    video: {
      deviceId: videoDeviceId ? { exact: videoDeviceId } : undefined,
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30 },
    },
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

export function stopStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((t) => t.stop());
}

/**
 * Attach a simple audio-level meter to a media stream. Returns a cleanup
 * function that stops the analyser. The meter invokes onLevel(level) with a
 * value between 0 and 1 approximately every animation frame.
 */
export function createMicActivityIndicator(stream, { onLevel, smoothing = 0.8 } = {}) {
  if (!stream || typeof window === 'undefined') return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) return null;

  const ctx = new AudioContext();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  analyser.smoothingTimeConstant = smoothing;
  source.connect(analyser);

  const data = new Uint8Array(analyser.frequencyBinCount);
  let running = true;
  let rafId = null;

  function tick() {
    if (!running) return;
    analyser.getByteFrequencyData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i += 1) sum += data[i];
    const average = sum / data.length / 255;
    if (onLevel) onLevel(average);
    rafId = requestAnimationFrame(tick);
  }

  tick();

  return function cleanup() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    try { source.disconnect(); } catch (_e) {}
    try { analyser.disconnect(); } catch (_e) {}
    try { ctx.close(); } catch (_e) {}
  };
}
